import './ManageUsers.css';
import './ManageReports.css';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { getReportTickets, updateReportTicketStatus, updateReportTicketComment, createNotificationMessage } from '../../api/communication';

function highlightText(text, query) {
    const str = String(text ?? '');
    if (!query) return str;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = str.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) => (
        i % 2 === 1 ? <span key={i} className="mu-search-highlight">{part}</span> : part
    ));
}

// The admin's own free-text comment gets echoed into the resolution notification's message,
// which the receiver's Notification.jsx renders via dangerouslySetInnerHTML -- escaped here since
// react-i18next's interpolation itself doesn't escape (see i18n.js's escapeValue: false, set so
// React's own text-node escaping isn't double-applied everywhere else t() is used).
function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function matchesQuery(ticket, query) {
    if (!query) return true;
    const q = query.toLowerCase();
    const name = `${ticket.first_name || ''} ${ticket.last_name || ''}`.trim();
    return (
        name.toLowerCase().includes(q) ||
        String(ticket.description || '').toLowerCase().includes(q)
    );
}

// Maps a ticket's report_type onto ModalReport.jsx's own translated labels, so the two never
// drift out of sync with each other.
const REPORT_TYPE_LABEL_KEYS = {
    bug: 'modalReport.reportTypeBug',
    content: 'modalReport.reportTypeContent',
    account: 'modalReport.reportTypeAccount',
    privacy: 'modalReport.reportTypePrivacy',
    feature: 'modalReport.reportTypeFeature',
    content_request: 'modalReport.reportTypeContentRequest',
    other: 'modalReport.reportTypeOther',
};

function ManageReports({ closeModal }) {
    const { t, i18n } = useTranslation();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    // 'open' | 'in_progress' | 'all' -- 'resolved' isn't a real tab since a solved ticket is
    // deleted outright (see handleMarkSolved) rather than lingering in that status.
    const [statusFilter, setStatusFilter] = useState('open');
    const [message, setMessage] = useState(null);
    const [confirmSolveId, setConfirmSolveId] = useState(null);
    const [busyId, setBusyId] = useState(null);
    // Keyed by ticket.id -- the admin's in-progress response, drafted in the comment input next
    // to a ticket's actions (only shown while that ticket is 'in_progress') until "Mark Solved"
    // is confirmed, at which point it's saved onto the ticket and echoed into the resolution
    // notification (see handleMarkSolved).
    const [commentDrafts, setCommentDrafts] = useState({});

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getReportTickets();
            setTickets(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch report tickets:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    useEffect(() => {
        const handleDocumentClick = (e) => {
            if (confirmSolveId !== null && !e.target.closest('[data-ticket-row]')) {
                setConfirmSolveId(null);
            }
        };
        document.addEventListener('click', handleDocumentClick);
        return () => document.removeEventListener('click', handleDocumentClick);
    }, [confirmSolveId]);

    const formatDate = (value) => {
        if (!value) return '';
        return new Intl.DateTimeFormat(i18n.language, {
            month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
        }).format(new Date(value));
    };

    const handleMarkInProgress = async (ticket) => {
        if (busyId) return;
        setBusyId(ticket.id);
        setMessage(null);
        try {
            await updateReportTicketStatus(ticket.id, 'in_progress');
            setTickets((prev) => prev.map((existing) => (existing.id === ticket.id ? { ...existing, status: 'in_progress' } : existing)));
        } catch (error) {
            console.error('Failed to update report ticket status:', error);
            setMessage({ type: 'error', text: t('manageReports.failedToUpdateStatus') });
        } finally {
            setBusyId(null);
        }
    };

    const handleMarkSolved = async (ticket) => {
        if (busyId) return;
        setBusyId(ticket.id);
        setMessage(null);
        try {
            const comment = (commentDrafts[ticket.id] || '').trim();
            const reportTypeLabel = t(REPORT_TYPE_LABEL_KEYS[ticket.report_type] || 'modalReport.reportTypeOther');

            if (comment) {
                // Persisted on the ticket first (see ReportTicket.comment in models.py) -- if
                // this fails, nothing below runs, so the admin can just retry rather than the
                // reporter being notified of a response that was never actually saved.
                await updateReportTicketComment(ticket.id, comment);
            }

            // Mark it resolved rather than deleting it -- the ticket needs to stick around so
            // the resolution notification's `report` link stays valid, letting the reporter
            // later "unsubmit" it from Notification.jsx. If THIS fails, the ticket (and its
            // just-saved comment) is left in place, still retryable, rather than silently
            // vanishing with no explanation sent.
            await updateReportTicketStatus(ticket.id, 'resolved');

            // `message` goes through dangerouslySetInnerHTML, so the admin's comment appended
            // to it below uses the escaped copy.
            let notifMessage = t('manageReports.resolvedNotifMessage', { type: reportTypeLabel });
            if (comment) {
                notifMessage += t('manageReports.resolvedNotifComment', { comment: escapeHtml(comment) });
            }

            await createNotificationMessage({
                receiver: ticket.user,
                subject: t('manageReports.resolvedNotifSubject'),
                message: notifMessage,
                is_important: false,
                type: 'report_solved',
                report: ticket.id,
            });
            setTickets((prev) => prev.filter((existing) => existing.id !== ticket.id));
            setCommentDrafts((prev) => {
                const next = { ...prev };
                delete next[ticket.id];
                return next;
            });
            setConfirmSolveId(null);
        } catch (error) {
            console.error('Failed to resolve report ticket:', error);
            setMessage({ type: 'error', text: t('manageReports.failedToResolve') });
        } finally {
            setBusyId(null);
        }
    };

    const filteredTickets = tickets
        .filter((ticket) => statusFilter === 'all' || ticket.status === statusFilter)
        .filter((ticket) => matchesQuery(ticket, search));

    return (
        <div className="mu-modal-overlay">
            <div className="mu-modal-box" style={{ width: '620px' }}>
                <h2>{t('manageReports.title')}</h2>

                <div className="mr-filter-row">
                    <div className="mr-tabs">
                        <button type="button" className={`mr-tab ${statusFilter === 'open' ? 'active' : ''}`} onClick={() => setStatusFilter('open')}>
                            {t('manageReports.filterOpen')}
                        </button>
                        <button type="button" className={`mr-tab ${statusFilter === 'in_progress' ? 'active' : ''}`} onClick={() => setStatusFilter('in_progress')}>
                            {t('manageReports.filterInProgress')}
                        </button>
                        <button type="button" className={`mr-tab ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>
                            {t('manageReports.filterAll')}
                        </button>
                    </div>
                    <input
                        type="text"
                        className="mu-login-input"
                        style={{ width: '200px', padding: '6px 12px', marginBottom: 0, fontSize: '0.85rem' }}
                        placeholder={t('manageReports.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {message && (
                    <div className={`mu-alert ${message.type === 'error' ? 'mu-alert-error' : 'mu-alert-success'}`}>
                        {message.text}
                    </div>
                )}

                <ul className="mu-user-list-container">
                    {!loading && filteredTickets.length === 0 && (
                        <li className="mu-empty-list-text">{t('manageReports.noReportsFound')}</li>
                    )}
                    {filteredTickets.map((ticket) => (
                        <li key={ticket.id} className="mr-ticket-item" data-ticket-row>
                            <div className="mr-ticket-header">
                                <span className={`mr-status-badge mr-status-${ticket.status}`}>
                                    {t(`manageReports.status_${ticket.status}`)}
                                </span>
                                <span className="mr-type-badge">
                                    {t(REPORT_TYPE_LABEL_KEYS[ticket.report_type] || 'modalReport.reportTypeOther')}
                                </span>
                                <span className="mr-ticket-date">{formatDate(ticket.created_at)}</span>
                            </div>
                            <div className="mr-ticket-reporter">
                                {t('manageReports.reportedBy')}: {highlightText(`${ticket.first_name} ${ticket.last_name}`.trim() || t('manageReports.anonymousReporter'), search)}
                            </div>
                            <p className="mr-ticket-description">{highlightText(ticket.description, search)}</p>
                            <div className="mr-ticket-footer">
                                {ticket.status !== 'resolved' && (
                                <div className="mr-ticket-actions">
                                    {ticket.status === 'open' && (
                                        <button
                                            type="button"
                                            className="mr-btn-progress"
                                            onClick={() => handleMarkInProgress(ticket)}
                                            disabled={busyId === ticket.id}
                                        >
                                            {t('manageReports.markInProgress')}
                                        </button>
                                    )}
                                    {confirmSolveId === ticket.id ? (
                                        <button
                                            type="button"
                                            className="mr-btn-confirm-solve"
                                            onClick={() => handleMarkSolved(ticket)}
                                            disabled={busyId === ticket.id}
                                        >
                                            {t('manageReports.confirmSolve')}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="mr-btn-solve"
                                            onClick={(e) => { e.stopPropagation(); setConfirmSolveId(ticket.id); }}
                                            disabled={busyId === ticket.id}
                                        >
                                            {t('manageReports.markSolved')}
                                        </button>
                                    )}
                                </div>
                                )}

                                {/* Only shown once a ticket is 'in_progress' -- the state where
                                    mr-ticket-actions above shows nothing but "Mark Solved" --
                                    so the admin writes their response right before resolving. */}
                                {ticket.status === 'in_progress' && (
                                    <textarea
                                        className="mr-comment-input"
                                        placeholder={t('manageReports.commentPlaceholder')}
                                        value={commentDrafts[ticket.id] || ''}
                                        onChange={(e) => setCommentDrafts((prev) => ({ ...prev, [ticket.id]: e.target.value }))}
                                        disabled={busyId === ticket.id}
                                    />
                                )}
                            </div>
                        </li>
                    ))}
                </ul>

                <div className="mu-modal-actions">
                    <button className="mu-btn-cancel" onClick={closeModal}>{t('manageReports.close')}</button>
                </div>
            </div>
        </div>
    );
}

export default ManageReports;
